import { getLocal } from '@/utils/storage';
import { safeJsonParse } from '@/utils/safeJsonParse';
import { STORAGE_KEY } from '@/constant/constant';

export interface CartItem {
  id: string | number;
  q: number;
}

export const getExcludedProductIds = (): string[] => {
  return safeJsonParse<string[]>(
    getLocal(STORAGE_KEY.EXCLUDE_PRODUCT_IDS) ?? '',
    [],
  );
};

export const filterExcludedItems = (
  items: CartItem[],
  excludedProductIds: string[],
) => {
  if (!excludedProductIds.length) return items;
  return items.filter((item) => !excludedProductIds.includes(String(item.id)));
};

/**
 * 对齐 master shoplazza/shopline 选品语义：
 * - 数量增加：记录该项但不中断，最终取「最后一个」qty 增加项
 * - 新商品：立即命中并中断
 */
export const findChangedItemId = (
  currentItems: CartItem[],
  oldItems: CartItem[],
) => {
  let changedId: CartItem['id'] | null = null;
  for (let i = 0; i < currentItems.length; i++) {
    const item = currentItems[i];
    const old = oldItems.find((x) => String(x.id) === String(item.id));
    if (!old) {
      return item.id;
    }
    if (old.q < item.q) {
      changedId = item.id;
    }
  }
  return changedId;
};

export const runWhenDocumentReady = (callback: () => void) => {
  if (
    window?.document?.readyState === 'interactive' ||
    window?.document?.readyState === 'complete'
  ) {
    callback();
    return;
  }
  const docCallback = () => {
    if (
      window?.document?.readyState === 'interactive' ||
      window?.document?.readyState === 'complete'
    ) {
      window?.document?.removeEventListener('readystatechange', docCallback);
      window?.document?.removeEventListener('DOMContentLoaded', docCallback);
      callback();
    }
  };
  window?.document?.addEventListener('readystatechange', docCallback);
  window?.document?.addEventListener('DOMContentLoaded', docCallback);
};

export const observeResourceRequests = (
  matcher: (url: string) => boolean,
  onMatch: () => void,
) => {
  if (!window.PerformanceObserver) return;
  const perfObserver = (list: PerformanceObserverEntryList) => {
    const resources = list.getEntries() as PerformanceResourceTiming[];
    const targets = resources.filter(
      (item) =>
        (item?.initiatorType === 'fetch' ||
          item?.initiatorType === 'xmlhttprequest') &&
        matcher(item?.name || ''),
    );
    if (targets.length > 0) onMatch();
  };
  try {
    const observer = new PerformanceObserver(perfObserver);
    observer.observe({ entryTypes: ['resource'] });
  } catch (error) {}
};

export const bindAddToCartButtonClick = (onClick: () => void) => {
  const listenerEvent = () => {
    setTimeout(() => onClick(), 500);
  };
  const handleEle = (elements: HTMLCollectionOf<Element>) => {
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i] as HTMLElement;
      const name = element.getAttribute('name');
      const type = element.getAttribute('type');
      const cssClass = element.getAttribute('class');
      const id = element.getAttribute('id');
      if (
        (id && id.toLowerCase().indexOf('addtocart') !== -1) ||
        (id && id.indexOf('add_to_cart') !== -1) ||
        (id && id.indexOf('add-to-cart') !== -1) ||
        (cssClass && cssClass.toLowerCase().indexOf('addtocart') !== -1) ||
        (cssClass && cssClass.indexOf('add_to_cart') !== -1) ||
        (cssClass && cssClass.indexOf('add-to-cart') !== -1) ||
        (name === 'add' && type === 'submit') ||
        (type === 'submit' &&
          element.innerText.toLowerCase().indexOf('add to cart') !== -1)
      ) {
        element.addEventListener('click', listenerEvent);
      }
    }
  };
  runWhenDocumentReady(() => {
    const buttons = document.getElementsByTagName('button');
    const inputs = document.getElementsByTagName('input');
    handleEle(buttons);
    handleEle(inputs);
  });
};
